// Step 5c (2026-11-03; Ahmed's answer 15, mockup Ro3jT69D7UncSpAvAJV2be approved 3 Nov, screen "What we owe each vendor"): a view under Purchase
// Orders, next to Invoices to check. Per vendor: what we owe for goods that arrived, money paid ahead for goods not here yet, what they hold of ours
// (Returns & credits), and what was bought on their credit that finance has not paid. The same calculation as each PO's Payments card.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Input, Typography, message } from 'antd';
import dayjs from 'dayjs';
import { getVendorBalances, VendorBalances as Balances } from '../../api';
import ResponsiveTable from '../responsive/ResponsiveTable';
import { FilterChips } from '../table-cells';
import { money } from '../receiving/shared';
import { tile } from '../account/ThisWeek';

const { Text } = Typography;
type Row = Balances['rows'][number];
const cell = (n: number) => (n > 0.5 ? <span style={{ fontVariantNumeric: 'tabular-nums' }}>{money(n)}</span> : <Text type="secondary">—</Text>);

const VendorBalances: React.FC = () => {
  const [data, setData] = useState<Balances | null>(null);
  const [chip, setChip] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const navigate = useNavigate();
  useEffect(() => { getVendorBalances().then(setData).catch((e) => message.error(e?.response?.data?.error || 'Could not load')); }, []);
  const rows = useMemo(() => (data?.rows || [])
    .filter((r) => (chip === 'owe' ? r.toPay > 0.5 : chip === 'hold' ? r.hold > 0.5 : chip === 'credit' ? r.credit > 0.5 : true))
    .filter((r) => !q.trim() || r.name.toLowerCase().includes(q.trim().toLowerCase())), [data, chip, q]);
  if (!data) return <Card loading />;
  const t = data.totals;
  const all = data.rows;
  const go = data.goLive;
  return (
    <div>
      <div style={{ fontSize: 13, color: '#5f6672', marginBottom: 12 }}>
        {go?.state === 'approved'
          ? `From the go-live date, ${dayjs(go.on).format('D MMM YYYY')}, plus the POs on the opening list. Older POs read "Before this system" and are not counted.`
          : null}
      </div>
      {go?.state !== 'approved' && <Alert type="warning" showIcon style={{ marginBottom: 12 }} message="The go-live date is not approved yet, so every PO counts here, also those paid outside the ERP." />}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 14 }}>
        {tile('We owe vendors', `EGP ${money(t.toPay)}`, 'received or part received, not paid')}
        {tile('Paid ahead, goods not here yet', `EGP ${money(t.paidAhead.value)}`, `${t.paidAhead.count} PO${t.paidAhead.count === 1 ? '' : 's'}`)}
        {tile('Vendors hold of ours', `EGP ${money(t.hold)}`, 'returns not refunded, paid over')}
        {tile("On a vendor's credit", `EGP ${money(t.credit)}`, 'finance pays')}
        {tile('In USD', `USD ${money(t.usd.value)}`, `${t.usd.count} PO${t.usd.count === 1 ? '' : 's'}, kept apart`)}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <FilterChips
          value={chip} onChange={setChip} allCount={all.length}
          chips={[
            { key: 'owe', label: 'We owe them', count: all.filter((r) => r.toPay > 0.5).length },
            { key: 'hold', label: 'They hold ours', count: all.filter((r) => r.hold > 0.5).length },
            { key: 'credit', label: 'On credit', count: all.filter((r) => r.credit > 0.5).length },
          ]}
        />
        <Input allowClear value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a vendor" aria-label="Find a vendor" style={{ width: 220 }} />
      </div>
      <Card size="small" bodyStyle={{ padding: 0 }} style={{ marginBottom: 8 }}>
        <ResponsiveTable<Row>
          rowKey="vendorId" size="small" pagination={{ pageSize: 50, hideOnSinglePage: true }} dataSource={rows}
          locale={{ emptyText: 'Nothing open with any vendor' }}
          card={{ header: ['name', 'toPay'], footer: ['open'] }}
          columns={[
            { title: 'Vendor', key: 'name', render: (_: any, r) => <bdi>{r.name}</bdi> },
            { title: 'POs open', dataIndex: 'posOpen', key: 'n', width: 90, align: 'right' as const },
            { title: 'To pay', key: 'toPay', width: 120, align: 'right' as const, render: (_: any, r) => cell(r.toPay) },
            { title: 'Paid ahead', key: 'ahead', width: 120, align: 'right' as const, render: (_: any, r) => cell(r.paidAhead) },
            { title: 'They hold of ours', key: 'hold', width: 140, align: 'right' as const, render: (_: any, r) => cell(r.hold) },
            { title: 'On credit', key: 'credit', width: 120, align: 'right' as const, render: (_: any, r) => cell(r.credit) },
            { title: 'Oldest unpaid', key: 'old', width: 140, align: 'right' as const, render: (_: any, r) => (r.oldest ? `${dayjs(r.oldest.on).format('D MMM')} · ${r.oldest.days} day${r.oldest.days === 1 ? '' : 's'}` : <Text type="secondary">—</Text>) },
            {
              title: '', key: 'open', width: 100, align: 'right' as const,
              render: (_: any, r) => <Button size="small" disabled={!r.poIds.length} onClick={() => navigate(`/pos?vendor=${r.vendorId}&ids=${r.poIds.join(',')}`)}>Its POs</Button>,
            },
          ]}
        />
      </Card>
      <Text type="secondary" style={{ fontSize: 12.5 }}>Its POs opens the PO list filtered to that vendor and to POs with money open. Amounts come from the same calculation as each PO's Payments card.</Text>
    </div>
  );
};

export default VendorBalances;
